import React, { useState } from "react";
import { FaPlus, FaTrashAlt } from "react-icons/fa";

const ConditionsSetup = ({ conditions, onChange, moduleFields }) => {
  const [conditionType, setConditionType] = useState("ALL"); // Default condition type: ALL
  const [conditionLogic, setConditionLogic] = useState(
    "And ALL of these conditions"
  );

  const addCondition = () => {
    onChange([
      ...conditions,
      {
        field: "Line BU/ DU", // Default Module Field
        operator: "Contains",
        value: "",
      },
    ]);
  };

  const updateCondition = (index, updatedCondition) => {
    const updatedConditions = conditions.map((cond, i) =>
      i === index ? updatedCondition : cond
    );
    onChange(updatedConditions);
  };

  const deleteCondition = (index) => {
    const updatedConditions = conditions.filter((_, i) => i !== index);
    onChange(updatedConditions);
  };

  const fixedOperatorOptions = [
    "Greater than",
    "Greater than or equals",
    "Less than",
    "Less than or equals",
    "Equals",
    "Between",
  ];

  const operatorOptions = [
    "Equals",
    "Starts with",
    "Contains",
    "Is blank",
    "Is not blank",
    "Not equal to",
    "Does not contain",
    "All equals",
    "All starts with",
    "All contains",
    "All is blank",
    "All is not blank",
    "All not equal to",
    "All does not contain",
  ];

  const conditionLogicOptions = [
    "And ALL of these conditions",
    "And ANY of these conditions",
    "And ALL of these conditions within the same line",
  ];

  return (
    <div className="p-4 bg-white rounded shadow-md space-y-6">
      <h2 className="text-lg font-semibold">Approval Chain Conditions</h2>

      <p className="text-sm text-gray-600">
        Select <strong>ALL</strong> of these conditions if you would like{" "}
        <strong>Total</strong> and all the other conditions to be met in the
        approval chain. Select <strong>ANY</strong> one of these conditions if
        you would like <strong>Total</strong> and any one of the other
        conditions to be met.
      </p>

      {/* Fixed First Condition */}
      <div className="flex flex-wrap items-center gap-4 border-b pb-4 mb-4">
        <div className="w-1/4">
          <label className="block text-sm font-medium">Field</label>
          <input
            type="text"
            value="Total"
            readOnly
            className="w-full border px-3 py-2 rounded bg-gray-100 cursor-not-allowed"
          />
        </div>

        <div className="w-1/4">
          <label className="block text-sm font-medium">Operator</label>
          <select
            onChange={(e) => console.log(e.target.value)}
            className="w-full border px-3 py-2 rounded"
          >
            {fixedOperatorOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="w-1/4">
          <label className="block text-sm font-medium">Value</label>
          <input
            type="text"
            defaultValue="0.00"
            className="w-full border px-3 py-2 rounded"
          />
        </div>

        <div className="w-1/4">
          <label className="block text-sm font-medium">Currency</label>
          <select
            value="INR"
            readOnly
            className="w-full border px-3 py-2 rounded bg-gray-100 cursor-not-allowed"
          >
            <option>INR</option>
          </select>
        </div>
      </div>

      {/* Condition Logic Selector */}
      <div className="flex items-center space-x-4 mb-4">
        <label className="text-sm font-medium">Condition Logic:</label>
        <select
          value={conditionLogic}
          onChange={(e) => setConditionLogic(e.target.value)}
          className="border px-3 py-2 rounded w-72"
        >
          {conditionLogicOptions.map((logic) => (
            <option key={logic} value={logic}>
              {logic}
            </option>
          ))}
        </select>
      </div>

      {/* Additional Conditions */}
      <div className="space-y-4">
        {conditions.map((cond, index) => (
          <div
            key={index}
            className="flex flex-wrap items-center gap-4 border-b pb-4 mb-4"
          >
            {/* Field Selector */}
            <div className="w-1/4">
              <label className="block text-sm font-medium">Field</label>
              <select
                value={cond.field}
                onChange={(e) =>
                  updateCondition(index, { ...cond, field: e.target.value })
                }
                className="w-full border px-3 py-2 rounded"
              >
                {moduleFields.map((field) => (
                  <option key={field} value={field}>
                    {field}
                  </option>
                ))}
              </select>
            </div>

            {/* Operator Selector */}
            <div className="w-1/4">
              <label className="block text-sm font-medium">Operator</label>
              <select
                value={cond.operator}
                onChange={(e) =>
                  updateCondition(index, { ...cond, operator: e.target.value })
                }
                className="w-full border px-3 py-2 rounded"
              >
                {operatorOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {/* Value Input */}
            <div className="w-1/4">
              <label className="block text-sm font-medium">Value</label>
              <input
                type="text"
                value={cond.value}
                onChange={(e) =>
                  updateCondition(index, { ...cond, value: e.target.value })
                }
                className="w-full border px-3 py-2 rounded"
                placeholder="e.g., ADMIN FACI"
              />
            </div>

            {/* Logical Connector */}
            <div className="w-1/4">
              <label className="block text-sm font-medium">Connector</label>
              <input
                type="text"
                value={
                  conditionLogic === "And ANY of these conditions"
                    ? "OR"
                    : "AND"
                }
                readOnly
                className="w-full border px-3 py-2 rounded bg-gray-100 cursor-not-allowed"
              />
            </div>

            {/* Delete Button */}
            <button
              onClick={() => deleteCondition(index)}
              className="text-red-500 hover:text-red-600 mt-6"
            >
              <FaTrashAlt />
            </button>
          </div>
        ))}

        {/* Add New Condition Button */}
        <button
          onClick={addCondition}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center space-x-2"
        >
          <FaPlus />
          <span>Add Condition</span>
        </button>
      </div>
    </div>
  );
};

export default ConditionsSetup;
